import React, { useEffect, useState, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  IconButton, 
  CircularProgress, 
  Alert, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogContentText, 
  DialogTitle,
  Chip,
  Card,
  CardContent,
  Grid,
  Fade
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Assignment as SurveyIcon,
  DateRange as DateIcon,
  Person as PersonIcon,
  Nature as CropIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { SeedSurvey } from '../types';

const SurveysPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [surveys, setSurveys] = useState<SeedSurvey[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [surveyToDelete, setSurveyToDelete] = useState<number | null>(null);

  const fetchSurveys = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getSurveys();
      setSurveys(response.results);
    } catch (err) {
      setError(t('loadError'));
      console.error('Failed to fetch surveys:', err);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchSurveys();
  }, [fetchSurveys]);

  const handleOpenDeleteDialog = (id: number) => {
    setSurveyToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setSurveyToDelete(null);
    setDeleteDialogOpen(false);
  };

  const handleDeleteSurvey = async () => {
    if (!surveyToDelete) return;

    try {
      await apiService.deleteSurvey(surveyToDelete);
      fetchSurveys(); // Refresh the list after deletion
    } catch (err) {
      setError(t('deleteError'));
      console.error('Failed to delete survey:', err);
    } finally {
      handleCloseDeleteDialog();
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '60vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={60} sx={{ color: '#667eea' }} />
        <Typography variant="h6" sx={{ color: '#64748b' }}>
          {t('loading')}
        </Typography>
      </Box>
    );
  }

  return (
    <Fade in timeout={600}>
      <Box sx={{ p: 0 }}>
        {/* Header Section */}
        <Box sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 3,
          p: 4,
          mb: 4,
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{
            position: 'absolute',
            top: -20,
            right: -20,
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
          }} />
          <Box sx={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.05)',
          }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
                📋 {t('surveysPageTitle')}
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400 }}>
                {t('surveysPageMessage')}
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => navigate('/survey/new')}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.3)',
                color: 'white',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                borderRadius: 2,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.3)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              {t('createSurvey')}
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              '& .MuiAlert-icon': {
                fontSize: '1.5rem'
              }
            }}
          >
            {error}
          </Alert>
        )}

        {/* Surveys List */}
        <Card sx={{ 
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid #f1f5f9'
        }}>
          <CardContent sx={{ p: 0 }}>
            <TableContainer>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow sx={{ 
                    bgcolor: '#f8fafc',
                    '& .MuiTableCell-head': {
                      fontWeight: 600,
                      color: '#374151',
                      fontSize: '0.95rem',
                      py: 2.5,
                      borderBottom: '2px solid #e5e7eb'
                    }
                  }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SurveyIcon sx={{ color: '#667eea', fontSize: '1.2rem' }} />
                        {t('surveyNumber')}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ color: '#667eea', fontSize: '1.2rem' }} />
                        {t('farmer')}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CropIcon sx={{ color: '#667eea', fontSize: '1.2rem' }} />
                        {t('crop')}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DateIcon sx={{ color: '#667eea', fontSize: '1.2rem' }} />
                        {t('collectionDate')}
                      </Box>
                    </TableCell>
                    <TableCell align="center">{t('actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {surveys.length > 0 ? (
                    surveys.map((survey, index) => (
                      <TableRow 
                        key={survey.id}
                        sx={{
                          '&:hover': {
                            bgcolor: '#f8fafc',
                            transform: 'scale(1.001)',
                          },
                          '& .MuiTableCell-root': {
                            py: 2,
                            borderBottom: '1px solid #f1f5f9'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>
                          {survey.survey_number}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon sx={{ color: '#6b7280', fontSize: '1.2rem' }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#374151' }}>
                                {survey.farmer_name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#6b7280' }}>
                                {survey.farmer_phone}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CropIcon sx={{ color: '#10b981', fontSize: '1.2rem' }} />
                            <Typography variant="body2" sx={{ color: '#374151' }}>
                              {i18n.language === 'ar' ? survey.crop_details?.name_ar : survey.crop_details?.name_en}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DateIcon sx={{ color: '#f59e0b', fontSize: '1.2rem' }} />
                            <Typography variant="body2" sx={{ color: '#374151' }}>
                              {new Date(survey.date_collected).toLocaleDateString(i18n.language === 'ar' ? 'ar-EG' : 'en-US')}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <IconButton 
                              onClick={() => navigate(`/survey/edit/${survey.id}`)}
                              size="small"
                              sx={{
                                bgcolor: '#f0f9ff',
                                color: '#0369a1',
                                '&:hover': {
                                  bgcolor: '#e0f2fe',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              onClick={() => navigate(`/survey/details/${survey.id}`)}
                              size="small"
                              sx={{
                                bgcolor: '#f0f9ff',
                                color: '#0369a1',
                                '&:hover': {
                                  bgcolor: '#e0f2fe',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              onClick={() => handleOpenDeleteDialog(survey.id)}
                              size="small"
                              sx={{
                                bgcolor: '#fef2f2',
                                color: '#dc2626',
                                '&:hover': {
                                  bgcolor: '#fee2e2',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center',
                          gap: 2,
                          color: '#9ca3af'
                        }}>
                          <SurveyIcon sx={{ fontSize: '4rem', opacity: 0.3 }} />
                          <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            {t('noData')}
                          </Typography>
                          <Typography variant="body2">
                            {t('surveysPageMessage')}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog 
          open={deleteDialogOpen} 
          onClose={handleCloseDeleteDialog}
          PaperProps={{
            sx: {
              borderRadius: 3,
              p: 1
            }
          }}
        >
          <DialogTitle sx={{ 
            fontWeight: 600, 
            color: '#dc2626',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <DeleteIcon />
            {t('deleteSurveyTitle')}
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: '#6b7280', fontSize: '1rem' }}>
              {t('deleteSurveyMessage')}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button 
              onClick={handleCloseDeleteDialog}
              sx={{ 
                color: '#6b7280',
                fontWeight: 500
              }}
            >
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleDeleteSurvey} 
              variant="contained"
              color="error"
              sx={{
                fontWeight: 600,
                px: 3
              }}
            >
              {t('delete')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Fade>
  );
};

export default SurveysPage;
